import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  icon: LucideIcon;
  iconClass?: string;
  label: string;
  value: string;
  subtitle?: string;
  onClick?: () => void;
  testId: string;
}

export function PrimaryStatCard({
  icon: Icon,
  iconClass,
  label,
  value,
  subtitle,
  onClick,
  testId,
}: Props) {
  const isInteractive = typeof onClick === "function";
  return (
    <Card
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        "flex items-center gap-4 px-5 py-4",
        isInteractive && "cursor-pointer hover:bg-muted/40 transition-colors"
      )}
      data-testid={testId}
    >
      <div
        className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
          iconClass ?? "bg-muted text-muted-foreground"
        )}
      >
        <Icon className="w-5 h-5" aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <h3 className="text-2xl font-bold leading-tight truncate">{value}</h3>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</p>
        )}
      </div>
      {isInteractive && (
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
      )}
    </Card>
  );
}
